/**
 * Parameter Naming Service
 *
 * Single source of truth for parameter name resolution.
 * Eliminates pattern matching and multi-strategy fallback chains by providing
 * a canonical name resolver that handles all parameter reference variations.
 *
 * This service consolidates:
 * - x-ms-summary variations (e.g., "Post as" -> "poster")
 * - x-ms-serialization parameterReference mappings
 * - Case-insensitive lookups
 * - Dynamic values/schema parameter references
 */

import type { ParameterInfo } from '@/types/operationParameter';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Result of resolving a parameter reference
 */
export interface ResolveResult {
  /** The canonical parameter name */
  canonicalName: string;
  /** How the resolution was achieved */
  source: 'direct' | 'swagger-mapping' | 'serialization-ref' | 'alias' | 'case-insensitive';
}

/**
 * Interface for swagger parser to support factory function
 */
export interface SwaggerParserLike {
  buildGlobalParameterNameMapping(): Map<string, string>;
}

// =============================================================================
// PARAMETER NAME RESOLVER
// =============================================================================

/**
 * ParameterNameResolver - Single source of truth for parameter name resolution
 *
 * This class maintains mappings from various parameter reference formats to
 * their canonical names, enabling consistent lookup across the application.
 *
 * Usage:
 * ```typescript
 * const resolver = createResolverFromSwagger(swaggerParser);
 * const result = resolver.resolve('PostAs');
 * // result = { canonicalName: 'poster', source: 'swagger-mapping' }
 * ```
 */
export class ParameterNameResolver {
  /** Map from lowercase reference -> canonical name */
  private referenceToCanonical = new Map<string, string>();

  /** Map from lowercase canonical name -> set of aliases */
  private canonicalToAliases = new Map<string, Set<string>>();

  /**
   * Resolve a parameter reference to its canonical name
   *
   * @param reference - The parameter reference to resolve (e.g., "PostAs", "poster", "body.poster")
   * @returns ResolveResult with canonical name and resolution source, or undefined if not found
   */
  resolve(reference: string): ResolveResult | undefined {
    if (!reference) return undefined;

    const lower = reference.toLowerCase();

    // Check if we have a mapping for this reference
    if (this.referenceToCanonical.has(lower)) {
      const canonicalName = this.referenceToCanonical.get(lower)!;
      const source: ResolveResult['source'] =
        canonicalName.toLowerCase() === lower ? 'direct' : 'swagger-mapping';
      return { canonicalName, source };
    }

    // Try stripping common prefixes (body., inputs., etc.)
    const prefixStripped = this.stripCommonPrefixes(lower);
    if (prefixStripped !== lower && this.referenceToCanonical.has(prefixStripped)) {
      return {
        canonicalName: this.referenceToCanonical.get(prefixStripped)!,
        source: 'alias',
      };
    }

    // Try extracting last segment for nested paths
    const lastSegment = lower.split('.').pop();
    if (lastSegment && lastSegment !== lower && this.referenceToCanonical.has(lastSegment)) {
      return {
        canonicalName: this.referenceToCanonical.get(lastSegment)!,
        source: 'alias',
      };
    }

    return undefined;
  }

  /**
   * Get all aliases for a canonical parameter name
   *
   * @param canonicalName - The canonical parameter name
   * @returns Array of aliases (excluding the canonical name itself)
   */
  getAliases(canonicalName: string): string[] {
    const aliases = this.canonicalToAliases.get(canonicalName.toLowerCase());
    return aliases ? Array.from(aliases) : [];
  }

  /**
   * Check if a reference can be resolved
   *
   * @param reference - The parameter reference to check
   * @returns true if the reference can be resolved
   */
  canResolve(reference: string): boolean {
    return this.resolve(reference) !== undefined;
  }

  /**
   * Register a parameter with its canonical name and aliases
   *
   * @param canonicalName - The canonical parameter name (as defined in swagger)
   * @param aliases - Additional names that should map to this parameter
   * @param serializationRef - Optional parameterReference from x-ms-serialization
   */
  registerParameter(
    canonicalName: string,
    aliases: string[] = [],
    serializationRef?: string
  ): void {
    const lowerCanonical = canonicalName.toLowerCase();

    // Register canonical name mapping to itself
    this.referenceToCanonical.set(lowerCanonical, canonicalName);

    // Initialize alias set if needed
    if (!this.canonicalToAliases.has(lowerCanonical)) {
      this.canonicalToAliases.set(lowerCanonical, new Set<string>());
    }
    const aliasSet = this.canonicalToAliases.get(lowerCanonical)!;

    // Register all aliases
    for (const alias of aliases) {
      if (alias) {
        const lowerAlias = alias.toLowerCase();
        this.referenceToCanonical.set(lowerAlias, canonicalName);
        aliasSet.add(alias);
      }
    }

    // Register serialization parameterReference if provided
    if (serializationRef) {
      const lowerRef = serializationRef.toLowerCase();
      this.referenceToCanonical.set(lowerRef, canonicalName);
      aliasSet.add(serializationRef);

      // Also register the last segment of the reference
      const lastSegment = serializationRef.split('.').pop();
      if (lastSegment && lastSegment.toLowerCase() !== lowerCanonical) {
        this.referenceToCanonical.set(lastSegment.toLowerCase(), canonicalName);
        aliasSet.add(lastSegment);
      }
    }
  }

  /**
   * Register a direct reference mapping
   *
   * @param reference - The reference to register
   * @param canonicalName - The canonical name it maps to
   */
  registerReference(reference: string, canonicalName: string): void {
    if (!reference || !canonicalName) return;
    this.referenceToCanonical.set(reference.toLowerCase(), canonicalName);

    // Add to alias set
    const lowerCanonical = canonicalName.toLowerCase();
    if (!this.canonicalToAliases.has(lowerCanonical)) {
      this.canonicalToAliases.set(lowerCanonical, new Set<string>());
    }
    this.canonicalToAliases.get(lowerCanonical)!.add(reference);
  }

  /**
   * Get the number of registered mappings (for debugging)
   */
  get size(): number {
    return this.referenceToCanonical.size;
  }

  /**
   * Get all registered canonical names (for debugging)
   */
  getCanonicalNames(): string[] {
    return Array.from(new Set(this.referenceToCanonical.values()));
  }

  /**
   * Strip common prefixes from a reference
   */
  private stripCommonPrefixes(reference: string): string {
    const prefixes = ['body.', 'inputs.', 'body.$.', 'inputs.$.'];
    for (const prefix of prefixes) {
      if (reference.startsWith(prefix)) {
        return reference.substring(prefix.length);
      }
    }
    return reference;
  }
}

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Create a ParameterNameResolver from a SwaggerParser
 *
 * Uses the swagger parser's buildGlobalParameterNameMapping() to get all
 * parameter name variations and registers them with the resolver.
 *
 * @param parser - SwaggerParser instance (or any object with buildGlobalParameterNameMapping)
 * @returns Configured ParameterNameResolver
 */
export function createResolverFromSwagger(parser: SwaggerParserLike): ParameterNameResolver {
  const resolver = new ParameterNameResolver();

  // Get the mapping from swagger parser
  const mapping = parser.buildGlobalParameterNameMapping();

  // Group aliases by canonical name
  const canonicalToAliases = new Map<string, string[]>();

  mapping.forEach((canonicalName, alias) => {
    if (!canonicalToAliases.has(canonicalName)) {
      canonicalToAliases.set(canonicalName, []);
    }
    canonicalToAliases.get(canonicalName)!.push(alias);
  });

  // Register all parameters with their aliases
  canonicalToAliases.forEach((aliases, canonicalName) => {
    resolver.registerParameter(canonicalName, aliases);
  });

  return resolver;
}

/**
 * Create a ParameterNameResolver from an array of ParameterInfo
 *
 * Extracts aliases from:
 * - label (x-ms-summary) variations
 * - serialization.property.parameterReference
 *
 * @param parameters - Array of ParameterInfo objects
 * @returns Configured ParameterNameResolver
 */
export function createResolverFromParameters(parameters: ParameterInfo[]): ParameterNameResolver {
  const resolver = new ParameterNameResolver();

  for (const param of parameters) {
    const aliases: string[] = [];

    // Add label as alias if different from parameterName
    if (param.label && param.label.toLowerCase() !== param.parameterName.toLowerCase()) {
      // "Post as" -> "Postas" and "postas"
      const labelNoSpaces = param.label.replace(/\s+/g, '');
      aliases.push(labelNoSpaces);

      // Also add camelCase version: "Post as" -> "postAs"
      const camelCase = param.label
        .split(/\s+/)
        .map((word, i) =>
          i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join('');
      if (camelCase !== labelNoSpaces.toLowerCase()) {
        aliases.push(camelCase);
      }
    }

    // Get serialization parameterReference if available
    const serializationRef = param.info?.serialization?.property?.parameterReference;

    resolver.registerParameter(param.parameterName, aliases, serializationRef);
  }

  console.log(
    `[createResolverFromParameters] Created resolver with ${resolver.size} mappings for ${parameters.length} parameters`
  );

  return resolver;
}

/**
 * Merge multiple resolvers into one
 *
 * Later resolvers take precedence over earlier ones for conflicting mappings.
 *
 * @param resolvers - Array of resolvers to merge
 * @returns New merged ParameterNameResolver
 */
export function mergeResolvers(...resolvers: ParameterNameResolver[]): ParameterNameResolver {
  const merged = new ParameterNameResolver();

  for (const resolver of resolvers) {
    for (const canonicalName of resolver.getCanonicalNames()) {
      const aliases = resolver.getAliases(canonicalName);
      merged.registerParameter(canonicalName, aliases);
    }
  }

  return merged;
}
